import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { MagnifyingGlass, CheckCircle, WarningOctagon, XCircle, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_META = {
  ok: { label: "OK", cls: "bg-emerald-50 text-emerald-800 border-emerald-300", icon: CheckCircle },
  missing: { label: "Missing from storage", cls: "bg-rose-50 text-rose-800 border-rose-300", icon: XCircle },
  no_record: { label: "No file record", cls: "bg-amber-50 text-amber-900 border-amber-300", icon: WarningOctagon },
  deleted: { label: "Deleted", cls: "bg-zinc-100 text-zinc-500 border-zinc-300", icon: Trash },
  error: { label: "Check failed", cls: "bg-amber-50 text-amber-900 border-amber-300", icon: WarningOctagon },
};

const PARENT_ROUTE = { expense: "/expenses", asset: "/assets", income: "/income" };

export default function FileHealth() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [onlyBroken, setOnlyBroken] = useState(true);

  const scan = async () => {
    setBusy(true);
    try {
      const r = await api.get("/files/orphans");
      setData(r.data);
      const s = r.data.summary;
      if (s.missing === 0 && s.no_record === 0 && s.errors === 0) {
        toast.success("All attachments verified — nothing missing.");
      } else {
        toast.warning(`${s.missing + s.no_record + s.errors} attachment(s) need attention.`);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Scan failed");
    } finally {
      setBusy(false);
    }
  };

  const items = (data?.items || []).filter((r) =>
    onlyBroken ? r.status !== "ok" && r.status !== "deleted" : true
  );

  return (
    <div className="p-4 lg:p-8 space-y-6" data-testid="file-health-root">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-end gap-3">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Administration</div>
          <h1 className="font-heading text-3xl sm:text-4xl mt-1 tracking-tight">File Health</h1>
          <p className="text-xs text-zinc-500 mt-2 max-w-2xl">
            Scans every attachment referenced by expenses, assets and income, and checks whether the
            file still exists in object storage. Re-upload anything marked <b>Missing</b> from the
            parent record page.
          </p>
        </div>
        <Button onClick={scan} disabled={busy} data-testid="run-scan-btn"
          className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white h-10 gap-2 w-full sm:w-auto">
          <MagnifyingGlass size={14} weight="bold" /> {busy ? "Scanning…" : "Run Scan"}
        </Button>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="scan-summary">
            {[
              ["Total references", data.summary.total_references, "text-zinc-900"],
              ["OK", data.summary.ok, "text-emerald-700"],
              ["Missing", data.summary.missing, "text-rose-700"],
              ["No record", data.summary.no_record, "text-amber-700"],
              ["Deleted", data.summary.deleted, "text-zinc-500"],
              ["Errors", data.summary.errors, "text-amber-700"],
            ].map(([label, val, cls]) => (
              <div key={label} className="bg-white border border-zinc-200 p-4"
                   data-testid={`summary-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
                <div className={`font-heading text-2xl tabular-nums mt-1 ${cls}`}>{val}</div>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2 text-sm">
            <input type="checkbox" id="only-broken" checked={onlyBroken}
                   onChange={(e) => setOnlyBroken(e.target.checked)}
                   data-testid="only-broken-toggle" />
            <label htmlFor="only-broken" className="text-zinc-700">Show only items needing attention</label>
          </div>

          <div className="bg-white border border-zinc-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-500">
                <tr>
                  <th className="text-left p-4">Status</th>
                  <th className="text-left p-4">File</th>
                  <th className="text-left p-4">Type</th>
                  <th className="text-left p-4">Attached To</th>
                  <th className="text-left p-4">Centre</th>
                  <th className="text-left p-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr><td colSpan={6} className="p-10 text-center text-zinc-400">
                    {onlyBroken ? "Nothing needs attention — all good." : "No file references found."}
                  </td></tr>
                )}
                {items.map((r, i) => {
                  const meta = STATUS_META[r.status] || STATUS_META.error;
                  const Icon = meta.icon;
                  return (
                    <tr key={`${r.file_id}-${i}`} className="border-t border-zinc-100"
                        data-testid={`file-health-row-${r.parent_no || r.file_id}`}>
                      <td className="p-4">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] uppercase tracking-widest border ${meta.cls}`}>
                          <Icon size={12} weight="bold" /> {meta.label}
                        </span>
                      </td>
                      <td className="p-4 max-w-[220px] truncate" title={r.filename}>{r.filename || "—"}</td>
                      <td className="p-4 text-zinc-600 capitalize">{r.kind}</td>
                      <td className="p-4">
                        <span className="capitalize text-zinc-600">{r.parent_kind}</span>{" "}
                        <span className="font-mono text-xs">{r.parent_no}</span>
                      </td>
                      <td className="p-4 text-zinc-600">{r.centre_code || "—"}</td>
                      <td className="p-4">
                        <Link to={`${PARENT_ROUTE[r.parent_kind]}/${r.parent_id}`}
                              data-testid={`reupload-link-${r.parent_no || r.file_id}`}
                              className="text-[10px] uppercase tracking-widest text-zinc-700 hover:text-black underline">
                          Open record →
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!data && (
        <div className="bg-white border border-zinc-200 p-16 text-center text-sm text-zinc-500">
          Run a scan to check every attachment against object storage.
        </div>
      )}
    </div>
  );
}
